import styled from 'styled-components';

import { IconBuilder } from '@components/icons/builder';
import { IconDropside } from '@components/icons/dropside';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

type ReasoningMessageProps = {
  id: string;
  timeTakenSeconds?: number;
  planSummary?: string;
  isExpanded: boolean;
  onToggle: () => void;
};

export const ReasoningMessage = ({
  timeTakenSeconds,
  planSummary,
  isExpanded,
  onToggle,
}: ReasoningMessageProps) => {
  const { t } = useScopedTranslate(locale);
  const hasPlanSummary = Boolean(planSummary);

  const handleClick = () => {
    if (hasPlanSummary) {
      onToggle();
    }
  };

  return (
    <ReasoningBadge
      onClick={handleClick}
      style={{
        cursor: hasPlanSummary ? 'pointer' : 'default',
      }}
    >
      <Stack orientation="horizontal" space={2} align="center">
        <IconBuilder color="elementPurple80" size={16} />
        <Text
          font="semibold12"
          text={t('reasonedFor', {
            seconds: timeTakenSeconds?.toFixed(1) ?? '0',
          })}
          color="textHelper"
        />
        {hasPlanSummary && (
          <IconDropside color="buttonIconPositive" size={16} rotateDegrees={isExpanded ? 90 : 0} />
        )}
      </Stack>
      {isExpanded && planSummary && (
        <ReasoningPlanContent>
          <Text font="regular14" text={planSummary} color="textSecondary" />
        </ReasoningPlanContent>
      )}
    </ReasoningBadge>
  );
};

const ReasoningBadge = styled.div({
  width: '100%',
  border: `1px solid ${theme.color.border10}`,
  borderRadius: pxToRem(8),
  padding: pxToRem(12),
  background: theme.color.backgroundLightDark,
});

const ReasoningPlanContent = styled.div({
  marginTop: pxToRem(12),
  paddingTop: pxToRem(12),
  borderTop: `1px solid ${theme.color.border10}`,
  width: '100%',
});

const locale = {
  en: {
    reasonedFor: 'Reasoned for {seconds}s',
  },
};
